import { globalDesktopHeight, globalDesktopSize } from 'globalConstants';
import CurrentUser from 'models/users/currentuser';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { IdentityProviderType } from 'models/identity/identityProvider';
import Tenant from 'models/tenants/tenant';
import React from 'react';
import AppError from 'utils/appError';

export interface IAppContext {
  useDarkMode: boolean;
  setUseDarkMode: (useDarkMode: boolean) => void;
  isContentLoading: boolean;
  showContentLoader: () => void;
  hideContentLoader: () => void;
  unhandledError?: AppError;
  error?: AppError;
  setError: (error: string | unknown) => void;
  showNotification: (
    msg: string | undefined,
    isError?: boolean,
    isWarning?: boolean,
    isBottom?: boolean,
    // Optional action link rendered after the message (e.g. a deeplink). onClick handles navigation so the
    // caller keeps control of where it goes.
    link?: { text: string; onClick: () => void },
  ) => void;
  hasScopes: (scopes: string[]) => Promise<boolean>;
  getAccessToken: (scopes: string[]) => Promise<string>;
  setUserProfile: () => Promise<void>;
  //whether the signed-in user has a Microsoft identity (signed in with Microsoft Entra ID). A function, because
  //the cache snapshots the context at login.
  hasMicrosoftIdentity: () => boolean;
  //sends a Microsoft Entra ID administrator to the admin consent of clientId for the scopes; the browser returns to
  //redirectUrl (a path of the app) with the outcome in the query (services/Auth/adminConsentParams.ts). Only the
  //Microsoft sign-in can start it; the other providers ignore the call.
  adminConsentScope: (clientId: string, scopes: string[], redirectUrl: string, stateSuffix?: string) => void;
  //starts a sign-in with the given identity provider
  //directoryId pins the directory: a Workspace domain for Google, an issuer URL for the organization sign-in
  loginWithProvider: (type: IdentityProviderType, directoryId?: string) => Promise<void>;
  isAuthenticated: boolean;
  isAuthInProgress: boolean;
  user: CurrentUser;
  refreshTenantList: () => Promise<void>;
  isMainNavCollapsed: boolean;
  toggleMainNavCollapsed: () => void;
  //the panel with the main menu on a phone-sized window
  isOpenSideBarPanel: boolean;
  toggleSideBarPanel: () => void;
  globalDataCache: GlobalDataCache;
  cacheMiss: (count: number) => Promise<void>;
  isMobileView: boolean;
  windowSize: number;
  windowHeight: number;
  startQuery: string | undefined;
  clearStartQuery: () => void;
  changeUserLanguage: (language: string) => Promise<string>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  switchOrg: (tenant: Tenant) => Promise<void>;
  switchOrgUnit: (tenant: Tenant) => Promise<void>;
  isOnboardingInProgress: boolean | undefined;
  setOnboardingInProgress: (inProgress: boolean) => void;
}

const appContext: IAppContext = {
  useDarkMode: false,
  setUseDarkMode: () => {},
  isContentLoading: false,
  showContentLoader: () => {},
  hideContentLoader: () => {},
  error: undefined,
  setError: () => {},
  showNotification: () => {},
  hasScopes: async () => false,
  getAccessToken: async () => '',
  setUserProfile: async () => {},
  hasMicrosoftIdentity: () => true,
  adminConsentScope: () => {},
  loginWithProvider: async () => {},
  isAuthenticated: false,
  isAuthInProgress: false,
  user: CurrentUser.getEmptyUser(),
  refreshTenantList: async () => {},
  isMainNavCollapsed: false,
  toggleMainNavCollapsed: () => {},
  isOpenSideBarPanel: false,
  toggleSideBarPanel: () => {},
  globalDataCache: new GlobalDataCache(),
  cacheMiss: async () => {},
  windowSize: globalDesktopSize,
  isMobileView: false,
  windowHeight: globalDesktopHeight,
  startQuery: undefined,
  clearStartQuery: () => {},
  changeUserLanguage: async () => '',
  login: async () => {},
  logout: async () => {},
  switchOrg: async () => {},
  switchOrgUnit: async () => {},
  isOnboardingInProgress: undefined,
  setOnboardingInProgress: () => {},
};

const AppContext = React.createContext(appContext);
AppContext.displayName = 'Global app context';

export default AppContext;
